import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useReads } from "@/contexts/ReadsContext";
import { SEARCH } from "@/constants/testIds";
import { Search as SearchIcon, Route, FileText, BookOpen } from "lucide-react";
import { ReadBadge } from "@/components/ReadIndicators";

const GROUP_ICON = {
  pathways: Route,
  blogs: FileText,
  terms: BookOpen,
};

export default function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { isRead } = useReads();

  useEffect(() => {
    setLoading(true);
    api.get(`/search?q=${encodeURIComponent(q)}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [q]);

  const groups = (data?.groups || []).filter(g => g.enabled);
  const totalHits = groups.reduce((sum, g) => sum + (g.results?.length || 0), 0);
  const emptyState = !loading && q && totalHits === 0;

  return (
    <div data-testid={SEARCH.page} className="max-w-5xl mx-auto px-6 md:px-10 py-16">
      <div className="text-xs uppercase tracking-[0.24em] text-sky-700 mb-3">Search</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 dark:text-slate-100">
        {q ? (
          <>Results for <span className="text-slate-500">&ldquo;{q}&rdquo;</span></>
        ) : (
          <>Start typing to search…</>
        )}
      </h1>
      {q && !loading && (
        <p className="mt-3 text-sm text-slate-500">
          {totalHits} {totalHits === 1 ? "match" : "matches"} across {groups.filter(g => g.results?.length).length} of {groups.length} sections
        </p>
      )}

      <div className="mt-10 space-y-14">
        {loading && <div className="text-slate-500">Searching…</div>}

        {emptyState && (
          <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-12 text-center">
            <SearchIcon className="w-6 h-6 mx-auto text-slate-400 mb-3"/>
            <div className="text-slate-600 dark:text-slate-300">No results found.</div>
            <div className="text-xs text-slate-400 mt-1">Try a different term.</div>
          </div>
        )}

        {!loading && q && groups.map(group => (
          <SearchGroup key={group.key} group={group} user={user} isRead={isRead}/>
        ))}
      </div>
    </div>
  );
}

function SearchGroup({ group, user, isRead }) {
  const Icon = GROUP_ICON[group.key] || SearchIcon;
  const results = group.results || [];
  if (results.length === 0) return null;

  return (
    <section data-testid={`search-group-${group.key}`}>
      <div className="flex items-baseline justify-between border-b border-slate-200 dark:border-slate-700 pb-3 mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-sky-700"/>
          <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100">
            {group.label}
          </h2>
        </div>
        <span className="text-xs uppercase tracking-widest text-slate-400">
          {results.length} {results.length === 1 ? "result" : "results"}
        </span>
      </div>
      <ol className="divide-y divide-slate-200 dark:divide-slate-700">
        {results.map((r, i) => {
          const done = user && group.key === "blogs" && isRead(r.id);
          return (
            <li key={r.id} data-testid={SEARCH.result(r.id)}>
              <Link to={r.href} className="group block py-5 flex gap-6 items-start hover:bg-slate-50 dark:hover:bg-slate-800 -mx-4 px-4 rounded">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-400 pt-1 min-w-[3ch]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className={`font-heading text-lg font-semibold group-hover:text-sky-700 ${done ? "text-slate-500 dark:text-slate-400" : "text-slate-900 dark:text-slate-100"}`}>
                      {r.title}
                    </div>
                    {done && <ReadBadge read/>}
                  </div>
                  {r.subtitle && (
                    r.subtitle_html
                      ? <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 leading-relaxed line-clamp-2 [&_mark]:bg-yellow-100 dark:[&_mark]:bg-yellow-500/25 [&_mark]:text-slate-900 dark:[&_mark]:text-yellow-100 [&_mark]:rounded [&_mark]:px-0.5"
                           dangerouslySetInnerHTML={{__html: r.subtitle}}/>
                      : <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 leading-relaxed line-clamp-2">
                          {r.subtitle}
                        </p>
                  )}
                </div>
              </Link>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
